import type { Symptom } from "$global-providers/rxdb-provider/collections/symptom";
import { useUser } from "$global-providers/user-provider";
import type { PatientClinicalResource } from "$routes/patient/[:patientId]/_lib/clinical-decision-support-service/db/collections/patient-clinical-resource";
import { useClinicalDecisionSupportService } from "$routes/patient/[:patientId]/_lib/clinical-decision-support-service/provider";
import { Box } from "@material-ui/core";
import { styled } from "@material-ui/core/styles";
import React, { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { FHIRResourceModule } from "./clinical-decision-support-service/db/collections/common";
import {
  type ChipOption,
  TextInputWithSelectableOptions,
} from "./text-input-with-selectable-options";

const Container = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
  marginTop: "20px",
  marginBottom: "20px",
}));

const SYMPTOM_ID_TO_ORDINAL_MAP = {
  // Fever
  "abd14942-adab-5c37-b1a0-f54d866c0b6e": 1,

  // Cough
  "31b4a559-bcf8-50a7-a671-7871ed3504de": 2,

  // Blood in urine
  "06a4a44d-d511-5095-bfe3-61ad617aa47d": 3,

  // Vomiting
  "09d850fa-076e-5309-bce9-1ba5e9a9ca9c": 4,

  // Diarrhoea
  "b79dc89c-652c-5f83-b880-01a41e47d04d": 5,

  // Hard to breathe
  "1e9d5c84-916c-50aa-9351-aabed744e4f0": 6,

  // Headache
  "38c97e4c-d753-59c0-997d-a460fcd55141": 7,

  // Stomach pain
  "f6069385-e051-5c7b-a328-224eae6ea4ca": 8,

  // Rash
  "6d784ccb-75a1-5af7-892f-987a37718436": 9,

  // Muscle ache
  "2b07cd5d-8a08-5d82-8aac-7a52dce2dfca": 10,

  // Sore throat
  "d4ae9861-0291-4097-9a32-215eaf5e04ea": 11,

  // Sore to urinate
  "cd0d1220-df3a-5f5b-9e01-87bac3797b24": 12,
};

const getSortedSymptomChipOptions = (
  availableSymptoms: Symptom[],
  symptomIdToOrdinalMap: Record<string, number>,
): Array<ChipOption> => {
  const sortedAvailableSymptoms = availableSymptoms.sort(
    (a, b) =>
      (symptomIdToOrdinalMap[a.id] || Number.MAX_SAFE_INTEGER) -
      (symptomIdToOrdinalMap[b.id] || Number.MAX_SAFE_INTEGER),
  );
  return sortedAvailableSymptoms.map((symptom, index) => ({
    label: symptom.name,
    optionId: symptom.id,
    index,
  }));
};
export const SymptomCaptureInput: React.FC<{
  availableSymptoms: Symptom[];
  symptoms: ChipOption[];
  addChip: (chip: ChipOption) => void;
  removeChip: (index: number) => void;
  loading: boolean;
  patientId: string;
  symptomIdToOrdinalMap?: Record<string, number>;
  resetInput: () => void;
}> = ({
  availableSymptoms,
  symptoms,
  addChip,
  removeChip,
  loading,
  patientId,
  symptomIdToOrdinalMap = SYMPTOM_ID_TO_ORDINAL_MAP,
  resetInput,
}) => {
  const user = useUser();
  const clinicalDecisionSupportContext = useClinicalDecisionSupportService();
  const [currentAvailableSymptomOptions, setCurrentAvailableSymptomOptions] =
    useState<ChipOption[]>([]);

  const sortedAvailableSymptoms = useMemo(
    () => getSortedSymptomChipOptions(availableSymptoms, symptomIdToOrdinalMap),
    [availableSymptoms, symptomIdToOrdinalMap],
  );

  const elephantSymptomToSnomedMapper = useMemo(
    () =>
      Object.fromEntries(
        availableSymptoms.map((symptom) => [
          symptom.id,
          `${symptom.externalCoding.snomed.system}/${symptom.externalCoding.snomed.code}`,
        ]),
      ),
    [availableSymptoms],
  );

  useEffect(() => {
    if (!symptoms.length && sortedAvailableSymptoms.length) {
      setCurrentAvailableSymptomOptions([sortedAvailableSymptoms[0]]);
    } else {
      const selectedIds = new Set(symptoms.map((s) => s.optionId));
      const remainingSymptoms = sortedAvailableSymptoms.filter(
        (symptom) => !selectedIds.has(symptom.optionId),
      );
      setCurrentAvailableSymptomOptions(remainingSymptoms);
    }
  }, [symptoms, sortedAvailableSymptoms[0]]);

  const addChipAndNotifyCDS = async (chip) => {
    addChip(chip);
    try {
      clinicalDecisionSupportContext.patientClinicalResources.add(
        "snomed",
        mapFormSymptomToCDSSymptom(chip),
      );
    } catch (error) {
      console.error(error, "Failed to add chip");
      removeChip(symptoms.length - 1);
    }
  };

  const removeChipAndNotifyCDS = async (index) => {
    // 'Fever' always at index position 0
    if (index === 0) {
      return resetInput();
    }
    const chip = symptoms[index];
    removeChip(index);
    try {
      clinicalDecisionSupportContext.patientClinicalResources.remove(
        elephantSymptomToSnomedMapper[chip.optionId],
      );
    } catch (error) {
      console.error(error, "Failed to remove chip");
      addChip(chip);
    }
  };

  const mapFormSymptomToCDSSymptom = (
    symptom: ChipOption,
  ): Omit<PatientClinicalResource, "id" | "patient" | "submissionStatus"> => {
    const currentDateTime = new Date().toISOString();

    return {
      fhirResource: {
        diagnosticReport: null,
        // TODO: standardise this and remove these magic strings
        resourceType: {
          module: FHIRResourceModule.Diagnostic,
          uri: "https://build.fhir.org/observation.html",
        },
        externalCoding: {
          snomed: {
            uri: elephantSymptomToSnomedMapper[symptom.optionId],
          },
          icd10: null,
          icd11: null,
          loinc: null,
          elephantInternal: null,
        },
      },
      meta: {
        createdAt: currentDateTime,
        updatedAt: currentDateTime,
        createdByUserId: user.id,
        updatedByUserId: user.id,
      },
    };
  };

  const { formatMessage } = useIntl();
  return (
    <Container>
      <TextInputWithSelectableOptions
        textFieldName="presentingComplaint"
        chipsFieldName="symptoms"
        availableOptions={currentAvailableSymptomOptions}
        placeholder={formatMessage({
          id: "patient.new.consultation.form.presentingComplaint.label",
          defaultMessage: "Presenting complaint",
        })}
        chips={symptoms}
        addChip={addChipAndNotifyCDS}
        removeChip={removeChipAndNotifyCDS}
        loading={loading}
      />
    </Container>
  );
};
